import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Divider, Avatar, 
  Grid, Card, CardContent, Button, TextField, 
  CircularProgress, Alert, Paper, Chip
} from '@mui/material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axios from 'axios';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CommentIcon from '@mui/icons-material/Comment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SEO from '../components/common/SEO';
import StructuredData from '../components/common/StructuredData';
import { getCanonicalUrl } from '../utils/seoConstants';
import { useAuthStore } from '../store/authStore';
import ThemedRichTextDisplay from '../components/common/ThemedRichTextDisplay';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [commentError, setCommentError] = useState(null);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  // Fetch blog post
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/blog/${slug}`);
        
        // Используем данные как есть, без преобразования URL
        const processedData = { ...response.data };
        
        setPost(processedData);
        setLoading(false);
        fetchComments(response.data.id);
      } catch (err) {
        setError('Не удалось загрузить статью. Пожалуйста, попробуйте позже.');
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Fetch comments
  const fetchComments = async (postId) => {
    try {
      setCommentsLoading(true);
      const response = await axios.get(`/api/blog/${postId}/comments`);
      setComments(response.data.comments);
      setCommentsLoading(false);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setCommentsLoading(false);
    }
  };

  // Handle comment submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentContent.trim()) {
      setCommentError('Пожалуйста, введите текст комментария');
      return;
    }
    
    try {
      const response = await axios.post(`/api/blog/${post.id}/comments`, {
        content: commentContent,
        parentCommentId: replyTo ? replyTo.id : null
      });
      
      setCommentContent('');
      setCommentSuccess(true);
      setReplyTo(null);
      
      // Show success message briefly
      setTimeout(() => {
        setCommentSuccess(false);
      }, 5000);
      
      // Refresh comments
      fetchComments(post.id);
    } catch (err) {
      console.error('Error submitting comment:', err);
      setCommentError(err.response?.data?.message || 'Не удалось отправить комментарий');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'd MMMM yyyy', { locale: ru });
  };

  // Handle reply to comment
  const handleReply = (comment) => {
    setReplyTo(comment);
    // Scroll to comment form
    document.getElementById('comment-form').scrollIntoView({ behavior: 'smooth' });
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyTo(null);
  };

  // Render comments recursively
  const renderComments = (comments, parentId = null, depth = 0) => {
    const filteredComments = comments.filter(comment => 
      parentId === null ? comment.parentCommentId === null : comment.parentCommentId === parentId
    );
    
    if (filteredComments.length === 0) return null;
    
    return (
      <Box sx={{ ml: depth > 0 ? 4 : 0 }}>
        {filteredComments.map((comment) => (
          <Box key={comment.id} sx={{ mb: 3 }}>
            <Paper elevation={depth > 0 ? 1 : 2} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Avatar 
                  src={comment.user?.avatar || ''} 
                  alt={comment.user?.name || 'User'}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {comment.user?.name || 'Пользователь'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(comment.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {comment.content}
                  </Typography>
                  {user && (
                    <Box sx={{ mt: 1, textAlign: 'right' }}>
                      <Button 
                        size="small" 
                        onClick={() => handleReply(comment)}
                        startIcon={<CommentIcon fontSize="small" />}
                      >
                        Ответить
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
            {/* Render nested comments */}
            {renderComments(comments, comment.id, depth + 1)}
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error" variant="h6">{error || 'Статья не найдена'}</Typography>
        <Button 
          component={Link} 
          to="/blog" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Вернуться к блогу
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* SEO метаданные */}
      <SEO 
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        keywords={post.metaKeywords}
        canonicalUrl={getCanonicalUrl(`/blog/${post.slug}`)}
        ogImage={post.featuredImage}
      />
      
      {/* Структурированные данные для поисковых систем */}
      <StructuredData 
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.featuredImage ? [post.featuredImage] : [],
          "datePublished": post.publishedAt,
          "dateModified": post.updatedAt,
          "author": {
            "@type": "Person",
            "name": post.author?.name || "DomikTyumen"
          },
          "publisher": {
            "@type": "Organization",
            "name": "DomikTyumen",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": getCanonicalUrl(`/blog/${post.slug}`)
          }
        }} 
      />
      
      {/* Навигация */}
      <Box sx={{ mb: 4 }}>
        <Button 
          component={Link} 
          to="/blog" 
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Вернуться к блогу
        </Button>
      </Box>
      
      {/* Заголовок и мета-информация */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
          {post.title}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(post.publishedAt)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {post.author?.name || 'DomikTyumen'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {post.viewCount} просмотров
            </Typography>
          </Box>
        </Box>
        
        {/* Краткое описание */}
        {post.excerpt && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                px: { xs: 1, sm: 3 },
                borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
                pl: 2,
                textAlign: 'left'
              }}
            >
              {post.excerpt}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Изображение */}
      <Box sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <img 
          src={post.featuredImage || '/assets/placeholder-blog.jpg'} 
          alt={post.title} 
          style={{ width: '100%', maxHeight: '500px', objectFit: 'cover' }}
          onLoad={() => console.log('Изображение загружено успешно')}
          onError={(e) => {
            console.log('Ошибка загрузки изображения, используем запасное');
            e.target.src = '/assets/placeholder-blog.jpg';
          }}
        />
      </Box>
      
      {/* Содержимое статьи */}
      <Box sx={{ mb: 6 }}>
        <ThemedRichTextDisplay content={post.content} />
      </Box>
      
      {/* Изображения статьи */}
      {post.images && post.images.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Галерея
          </Typography>
          <Grid container spacing={2}>
            {post.images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Box 
                  sx={{ 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    height: 200,
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover img': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <img 
                    src={image.url || image.imageUrl || '/assets/placeholder-blog.jpg'} 
                    alt={image.altText || image.caption || post.title}
                    onError={(e) => {
                      console.log('Ошибка загрузки изображения в галерее, используем запасное');
                      e.target.src = '/assets/placeholder-blog.jpg';
                    }}
                  />
                </Box>
                {image.caption && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {image.caption}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      <Divider sx={{ my: 4 }} />
      
      {/* Секция комментариев */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Комментарии ({comments.length})
        </Typography>
        
        {commentsLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Пока нет комментариев. Будьте первым!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            {renderComments(comments)}
          </Box>
        )}
        
        {/* Форма комментария */}
        <Box id="comment-form" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            {replyTo ? `Ответ на комментарий ${replyTo.user?.name || 'пользователя'}` : 'Оставить комментарий'}
          </Typography>
          
          {!user ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Чтобы оставить комментарий, пожалуйста, <Link to="/login">войдите</Link> или <Link to="/register">зарегистрируйтесь</Link>.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleCommentSubmit}>
              {replyTo && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Ответ на комментарий: "{replyTo.content.substring(0, 100)}{replyTo.content.length > 100 ? '...' : ''}"
                  <Button size="small" onClick={cancelReply} sx={{ ml: 2 }}>
                    Отменить
                  </Button>
                </Alert>
              )}
              
              {commentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {commentError}
                </Alert>
              )}
              
              {commentSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Ваш комментарий успешно отправлен и будет опубликован после проверки модератором.
                </Alert>
              )}
              
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Введите ваш комментарий..."
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={!commentContent.trim()}
              >
                Отправить комментарий
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default BlogPostPage;
